import { defaultAiAssistantInfo } from "@memorang/helpers/src/constants";
import type { Apps } from "types/apps";
import { z } from "zod";

const tenantSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const internalizationSchema = z.record(z.string(), z.string());

const masteryLabelsSchema = z.object({
	low: z.string(),
	medium: z.string(),
	high: z.string(),
	none: z.string(),
});

const landingSchema = z.object({
	title: z
		.string()
		.or(
			z
				.object({
					prefix: z.string(),
					decoratedText: z.string(),
					suffix: z.string(),
				})
				.partial(),
		)
		.optional(),
	marketText: z
		.object({
			prefix: z.string().optional(),
			suffix: z.string().optional(),
		})
		.optional(),
	subtitle: z.string().optional(),
	valueProps: z
		.array(
			z.object({
				icon: z.string(),
				title: z.string(),
				subtitle: z.string(),
			}),
		)
		.optional(),
	landingGraphics: z.string().optional(),
});

const scoreRangeSchema = z.object({
	rangeArray: z.array(z.number()),
	range: z.string(),
	color: z.string(),
	icon: z.string(),
});

const fontSizesSchema = z.object({
	stem: z.number(),
	answer: z.number(),
});

const purchaseSchema = z.object({
	isSubscriptionEnabled: z.boolean().optional(),
	subheading: z.string().nullable().optional(),
	baseSku: z.string().optional(),
});

const logoSchema = z.object({
	stacked: z.object({
		// todo: might be more complex schema for image
		light: z.object({
			uri: z.string(),
		}),
		dark: z.object({
			uri: z.string(),
		}),
	}),
	default: z.object({
		light: z.object({
			uri: z.string(),
		}),
		dark: z.object({
			uri: z.string(),
		}),
	}),
	company: z
		.object({
			light: z.object({
				uri: z.string(),
			}),
			dark: z.object({
				uri: z.string(),
			}),
		})
		.optional(),
});

const linksSchema = z.object({
	baseUrl: z.string().optional(),
	privacy: z.string(),
	tos: z.string(),
	ios: z.string().optional(),
	android: z.string().optional(),
	redeemSupportUrl: z.string().optional(),
	companyUrl: z.string().optional(),
});

const revenueCatSchema = z.object({
	ios: z.string(),
	android: z.string(),
});

const appAnalyticsSchema = z.object({
	mixpanel: z.object({
		id: z.string(),
	}),
	analyticsDestinations: z
		.array(
			z.object({
				type: z.enum(["mixpanel", "segment"]),
				apiKey: z.string(),
				name: z.string().optional(),
				projectId: z.string().optional(),
			}),
		)
		.optional(),
});

const clerkSchema = z.object({
	publishableKeyDev: z.string(),
	publishableKeyProd: z.string(),
});

const appKeysSchema = z.object({
	revenueCat: revenueCatSchema,
	oneSignalAppId: z.string(),
	clerk: clerkSchema.optional().nullable(),
	airtableBase: z.string().optional(),
});

const sessionConfigFiltersSchema = z.array(
	z.object({
		label: internalizationSchema,
		distKey: z.string(),
		color: z.string().optional(),
	}),
);

export type SessionConfigFiltersType = z.infer<
	typeof sessionConfigFiltersSchema
>;

const sessionConfigSchema = z.object({
	showDistributionFilters: z.boolean().optional(),
	maxItemsPerSession: z.number().optional(),
	showItemsSlider: z.boolean().optional(),
	showLearningMode: z.boolean().optional(),
	filters: sessionConfigFiltersSchema.optional(),
	crossTagSelection: z.boolean().optional(),
});

const welcomeDialogSchema = z.object({
	diagnostic: z.object({
		title: z.string(),
		body: z.string(),
	}),
	disclaimer: z.string().optional(),
});

const elevenLabsSchema = z.object({
	modelId: z.string(),
	voiceId: z.string(),
});
export type WelcomeDialogSchemaType = z.infer<typeof welcomeDialogSchema>;
const sessionViewConfigSchema = z.object({
	cta: z.string().optional(),
	disclaimer: z.string().optional(),
	welcomeDialog: welcomeDialogSchema.optional(),
	showFlightCalculator: z.boolean().optional().default(false),
	maxItems: z
		.object({
			flashcards: z.number(),
			questions: z.number(),
		})
		.optional(),
	postAnswerStats: z
		.object({
			showXpEarned: z.boolean().optional(),
			showNumAttempts: z.boolean().optional(),
		})
		.optional(),
	timer: z.boolean().optional(),
	showCrossOut: z.boolean().optional().default(true),
	selectionStrategy: z
		.object({
			default: z.string(),
			optimized: z.boolean(),
			random: z.boolean(),
			shuffled: z.boolean(),
		})
		.optional(),
	suggestCorrection: z
		.object({
			description: z.string(),
		})
		.optional(),
});

const statsSchema = z.array(
	z.object({
		type: z.string(),
		title: z.string(),
		icon: z.string(),
		tooltip: z.string().optional(),
	}),
);

export type AppConfigStatsType = z.infer<typeof statsSchema>;

const cardSchema = z.object({
	type: z.string(),
	title: internalizationSchema.optional(),
});

export type CardSchemaType = z.infer<typeof cardSchema>;

const cardsSchema = z.array(cardSchema);

export type AppConfigCardsType = z.infer<typeof cardsSchema>;

const accountSettingsViewSchema = z.object({
	showFaqs: z.boolean().optional(),
	showPurchasedProducts: z.boolean().optional(),
	showCurrentExam: z.boolean().optional(),
	showLanguageSettings: z.boolean().optional(),
	showAudioTestDialog: z.boolean().optional(),
	showPartnersSection: z.boolean().optional(),
});

const reportViewConfigSchema = z.object({
	practiceTest: z.object({
		reviewEnabled: z.boolean(),
	}),
	stats: statsSchema.optional(),
	cards: cardsSchema.optional(),
	formative: z
		.object({
			showPointsBasedScore: z.boolean().optional(),
			maxScore: z.number().optional(),
			reviewEnabled: z.boolean().optional(),
		})
		.optional(),
});

export const onboardingDatItemSchema = z.object({
	title: z.string().optional(),
	image: z.string().optional(),
	type: z.string(),
	id: z.string().optional(),
	description: z.string().optional(),
	label: z.string().optional(),
	icon: z.string().optional(),
});

export type OnboardingDataItem = z.infer<typeof onboardingDatItemSchema>;

const onboardingDataSchema = z.array(onboardingDatItemSchema);

const baseItemSchema = z.object({
	label: z.string(),
	value: z.string(),
	icon: z.string().optional(),
	defaultSelectedIds: z.array(z.string()).optional(),
});

const itemsOnboardingSchema = z.array(
	z.object({
		...baseItemSchema.shape,
		items: z.array(baseItemSchema).optional(),
	}),
);
const onboardingStepSchema = z.object({
	step: z.string(),
	enabled: z.boolean(),
	title: z.string(),
	numMaxSelect: z.number().optional(),
	description: z.string().optional(),
	descriptionMobile: z.string().optional(),
	secondaryCta: z.string().optional(),
	isMobile: z.boolean().optional(),
	itemsData: z
		.object({
			defaultSelectedIds: z.array(z.string()).optional(),
			items: itemsOnboardingSchema,
		})
		.optional(),
	suffix: z.string().optional(),
	data: onboardingDataSchema.optional(),
	cta: z.string().optional(),
	graphics: z
		.object({
			desktop: z.string(),
			tablet: z.string().optional(),
			mobile: z.string().optional(),
		})
		.optional(),
	demo: z
		.object({
			desktop: z.string(),
			mobile: z.string().optional(),
		})
		.optional(),
});

export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

const navigationRouteSchema = z.object({
	title: z.string(),
	name: z.string(),
	icon: z.object({
		default: z.string(),
		selected: z.string(),
	}),
	enabled: z.boolean(),
	custom: z.boolean().optional(),
});

const navigationSchema = z.object({
	routes: z.object({
		main: z.array(navigationRouteSchema),
	}),
});

const oldPortalSchema = z.object({
	title: z.string(),
	description: z.string(),
	link: z.string(),
});

const aiAssistantSchema = z.object({
	title: z.string().optional().default(defaultAiAssistantInfo.title),
	alias: z.string().optional().default(defaultAiAssistantInfo.alias),
	icon: z.string(),
	showOriginalQuestion: z
		.boolean()
		.optional()
		.default(defaultAiAssistantInfo.showOriginalQuestion),
	initialMessage: z
		.string()
		.optional()
		.default(defaultAiAssistantInfo.initialMessage),
	initialAiMessage: internalizationSchema.optional(),
});

const masteryLevelsSchema = z.array(
	z.object({
		id: z.string(),
		darkColor: z.string(),
		lightColor: z.string(),
		sortOrder: z.number(),
		icon: z.string(),
		range: z.object({
			type: z.string(),
			score: z.array(z.number()),
			points: z.array(z.number()),
		}),
		alias: z.object({
			title: z.string(),
		}),
		level: z.string(),
	}),
);

export type AppConfigMasteryLevelsType = z.infer<typeof masteryLevelsSchema>;

const fontSchema = z.enum([
	"opensans",
	"beausite",
	"greetnarrow",
	"greetstandard",
	"sourcesanspro",
	"calibri",
]);

export type FontSchemaType = z.infer<typeof fontSchema>;

const appConfigSchema = z.object({
	id: z.string(),
	name: z.string(),
	maintenance: z
		.object({
			enabled: z.boolean(),
			title: z.string().optional().default("We'll be right back"),
			urls: z
				.object({
					ios: z.string().optional(),
					android: z.string().optional(),
				})
				.optional(),
			subtitle: z
				.string()
				.optional()
				.default(
					"We're currently making improvements to enhance your experience. We apologize for any inconvenience and appreciate your patience.",
				),
		})
		.optional(),

	font: fontSchema.optional().default("opensans"),
	trademark: z.string().optional(),
	showDiagnostic: z.boolean().optional(),
	showPracticeTests: z.boolean().optional(),
	purchase: purchaseSchema.optional(),
	masteryLabels: masteryLabelsSchema.optional(),
	masteryLevels: masteryLevelsSchema.optional(),
	scoreRanges: z.array(scoreRangeSchema).optional(),
	fontSizes: fontSizesSchema.optional(),
	componentOverrides: z.record(z.string(), z.any()).optional(),
	analytics: appAnalyticsSchema,
	logo: logoSchema,
	links: linksSchema,
	keys: appKeysSchema,
	supportUrl: z.string().optional(),
	tagType: internalizationSchema.optional().default({
		"en-US": "topic",
	}),
	oldPortal: oldPortalSchema.optional(),
	aiAssistant: aiAssistantSchema.optional().default(defaultAiAssistantInfo),
	isImageChallengeApp: z.boolean().optional().default(false),
	isStudyPackEnabled: z.boolean().optional().default(true),
	landing: landingSchema.optional(),
	showCountrySelection: z.boolean().optional().default(false),
	avatarType: z.enum(["initials", "identicon"]).optional(),
	sessionConfig: sessionConfigSchema.optional(),
	accountSettings: accountSettingsViewSchema.optional().nullable(),
	elevenLabs: elevenLabsSchema.optional(),
	appAvailabilityRegions: z
		.array(z.string())
		.optional()
		.describe(
			"Array of ISO 3166-1 alpha-2 country codes where app is available. Empty or undefined means available everywhere",
		),
});

const themeSchema = z.record(z.string(), z.any());

const alertItemSchema = z.object({
	description: z.string(),
	icon: z.string(),
	severity: z.enum(["info", "warning", "error"]),
	title: z.string().optional(),
});

const defaultAlertConfigs = {
	quiz: {
		severity: "info" as const,
		icon: "format-list-checks",
		description:
			"Start with a short, free diagnostic to familiarize yourself with the test format.",
	},
	practiceTest: {
		severity: "info" as const,
		icon: "clipboard-text-outline",
		description:
			"Assess your readiness with timed, full-length practice tests. Get detailed explanations, personalized feedback, and a score report after you finish.",
	},
	quizAndPracticeTest: {
		severity: "info" as const,
		icon: "format-list-checks",
		description:
			"Start with a free diagnostic to familiarize yourself with the test format or assess your readiness with timed, full-length practice tests. Get detailed explanations, personalized feedback, and a score report after you finish.",
	},
	topicMastery: {
		severity: "info" as const,
		icon: "chart-line",
		title: "Mastery Breakdown",
		description:
			"The table below shows your performance on each topic, the number of items you've answered so far, the proportion of the exam that the topic represents, and your opportunity for improvement. The greater the 'opportunity,' the better suited you will be by focusing your studies to strengthen your mastery of that topic to maximize your chance of passing.",
	},
};

const alertConfigSchema = z.object({
	quiz: alertItemSchema.default(defaultAlertConfigs.quiz),
	practiceTest: alertItemSchema.default(defaultAlertConfigs.practiceTest),
	quizAndPracticeTest: alertItemSchema.default(
		defaultAlertConfigs.quizAndPracticeTest,
	),
	topicMastery: alertItemSchema.default(defaultAlertConfigs.topicMastery),
});

const summativeTestAlertConfigSchema = alertConfigSchema
	.optional()
	.default(defaultAlertConfigs);

export type SummativeTestAlertConfigType = z.infer<typeof alertConfigSchema>;

const practiceTestConfigSchema = z
	.object({
		label: z.string().optional().default("Practice Tests"),
		summativeTestAlertConfig: summativeTestAlertConfigSchema,
		enabled: z
			.object({
				mobile: z.boolean().default(false),
				web: z.boolean().default(true),
			})
			.optional()
			.default({
				mobile: false,
				web: true,
			}),
	})
	.optional();

export const customConfigTypeSchema = z.object({
	tenant: tenantSchema,
	supportEmail: z.string(),
	companyName: z.string(),
	app: appConfigSchema,
	groupByCategory: z.boolean().optional().nullable(),
	exam: z.object({
		enabled: z.boolean(),
		quizEnabled: z.boolean().optional(),
		allowChangeExam: z.boolean().optional(),
		label: z.string().optional(),
		diagnosticLabel: z.record(z.string(), z.string()).optional(),
		taskCardSubtitle: z.string().optional(),
		practiceTest: practiceTestConfigSchema.optional().default({
			label: "Practice Tests",
			summativeTestAlertConfig: defaultAlertConfigs,
		}),
		examDateDisclaimer: z
			.string()
			.optional()
			.default(
				"This date is only to guide your study schedule within the app. It does not affect your official exam registration.",
			),
	}),
	store: z.object({
		enabled: z.boolean(),
	}),
	homeView: z
		.object({
			showRecommendedChallenge: z.boolean(),
			showArticles: z.boolean(),
			showChallengeOfTheWeek: z.boolean(),
			masterySubheaderLabel: z.string().optional(),
			showRedeem: z.boolean().optional(),
		})
		.optional()
		.nullable(),
	showStudyPackAsHome: z.boolean().optional().nullable(),
	reportView: reportViewConfigSchema.optional().nullable(),
	onboarding: z.array(onboardingStepSchema).optional().nullable(),
	navigation: navigationSchema,
	sessionView: sessionViewConfigSchema.optional().nullable(),
	theme: themeSchema,
});

export type CustomConfigType = z.infer<typeof customConfigTypeSchema>;

export type ConfigMap = Record<Apps, CustomConfigType>;
